import React, { useState, useEffect } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Check, Camera, Microphone, ArrowLeft, ShieldCheck, FilmStrip, Envelope, Lightning, DeviceMobile, QrCode, BookOpen, Star } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCT_CATALOG = {
  capture: { name: 'QR Photo Upload', price: 29, icon: 'QrCode', desc: 'QR code + live gallery + unlimited uploads', color: '#C29D52' },
  guestbook: { name: 'Video Guestbook', price: 39, icon: 'Microphone', desc: 'Audio & video guest messages', color: '#26A69A' },
  film: { name: 'Event Film', price: 49, icon: 'FilmStrip', desc: 'Cinematic film from all content', color: '#A033D8' },
  film_premium: { name: 'Premium Event Film', price: 59, icon: 'FilmStrip', desc: 'AI voiceover + premium edit', color: '#A033D8' },
  highlight: { name: 'Highlight Reel', price: 12, icon: 'DeviceMobile', desc: '30s vertical for Instagram & TikTok', color: '#A855F7' },
  invites: { name: 'Invites', price: 29, icon: 'Envelope', desc: 'Send invites & track responses', color: '#B89762' },
  challenges: { name: 'Challenge Mode', price: 29, icon: 'Lightning', desc: 'Live challenges + leaderboards', color: '#B89762' },
  booth: { name: 'Photo Booth', price: 79, icon: 'Camera', desc: 'Voice-guided photo/video booth', color: '#C29D52' },
  pocket_booth: { name: 'Pocket Booth', price: 49, icon: 'Camera', desc: 'Real-time photo booth with sync capture, QR join & event film', color: '#FF0099' },
  spin_360: { name: 'Spin Film 360', price: 49, icon: 'Camera', desc: 'Slow-mo 360 boomerangs guests record + share live', color: '#FF6BCB' },
  memory_book: { name: 'Memory Guest Book', price: 29, icon: 'BookOpen', desc: 'A5 PDF keepsake: guest messages + photo gallery. Arrange print anywhere.', color: '#C29D52' },
  pro_upgrade: { name: 'Pro Upgrade', price: 9.99, icon: 'Star', desc: 'Remove watermark + premium music library', color: '#FFCC00' },
};

const ICON_MAP = {
  QrCode: <QrCode size={22} weight="bold" className="text-white" />,
  Camera: <Camera size={22} weight="bold" className="text-white" />,
  Microphone: <Microphone size={22} weight="bold" className="text-white" />,
  FilmStrip: <FilmStrip size={22} weight="bold" className="text-white" />,
  Envelope: <Envelope size={22} weight="bold" className="text-white" />,
  Lightning: <Lightning size={22} weight="bold" className="text-white" />,
  DeviceMobile: <DeviceMobile size={22} weight="bold" className="text-white" />,
  BookOpen: <BookOpen size={22} weight="bold" className="text-white" />,
  Star: <Star size={22} weight="bold" className="text-white" />,
};

const PACKAGES = {
  // ── Current 3-tier lineup (May 2026) — Essentials / Experience / Premium ──
  // Slugs match `EVENT_TYPE_TO_THEME` and backend `PACKAGE_PRICING`.
  essentials: {
    name: 'Essentials', price: 39,
    products: ['capture', 'invites', 'highlight'],
  },
  experience: {
    name: 'Experience', price: 145,
    products: ['capture', 'invites', 'highlight', 'memory_book',
               'guestbook', 'pocket_booth', 'challenges'],
  },
  premium: {
    name: 'Premium', price: 245,
    products: ['capture', 'invites', 'highlight', 'memory_book',
               'guestbook', 'pocket_booth', 'spin_360', 'challenges',
               'film', 'pro_upgrade'],
  },
  // ── Retained for in-flight old-URL sessions ──
  // Old slugs keep resolving so a Stripe redirect or shared link from
  // before the May 2026 tier rename still works. Don't change prices.
  capture: { name: 'The Capture (legacy)', price: 39, products: ['capture', 'highlight'] },
  legacy: {
    name: 'The Legacy (legacy)', price: 249,
    products: ['capture', 'pocket_booth', 'guestbook', 'challenges',
               'highlight', 'film', 'booth', 'invites',
               'pro_upgrade', 'memory_book'],
  },
  snap: { name: 'The Snap (legacy)', price: 49, products: ['capture'] },
  story: { name: 'The Story (legacy)', price: 99, products: ['capture', 'guestbook', 'highlight'] },
};

const PreCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [activePackage, setActivePackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const pkg = searchParams.get('package');
    if (pkg && PACKAGES[pkg]) {
      setActivePackage(pkg);
      setSelectedProducts(new Set(PACKAGES[pkg].products));
      return;
    }
    const adds = searchParams.getAll('add');
    const product = searchParams.get('product');
    const initial = new Set();
    if (product && PRODUCT_CATALOG[product]) initial.add(product);
    adds.forEach(id => { if (PRODUCT_CATALOG[id]) initial.add(id); });
    initial.add('capture');
    setSelectedProducts(initial);
  }, [searchParams]);

  const toggleProduct = (id) => {
    if (id === 'capture') return; // Can't remove base product
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const subtotal = activePackage ? PACKAGES[activePackage].price : [...selectedProducts].reduce((sum, id) => sum + (PRODUCT_CATALOG[id]?.price || 0), 0);
  const promoDiscount = promoApplied ? (promoApplied.discount === 100 ? subtotal : Math.round(subtotal * promoApplied.discount / 100)) : 0;
  const total = subtotal - promoDiscount;

  const addOnProducts = Object.entries(PRODUCT_CATALOG).filter(([id]) => id !== 'capture' && id !== 'film_premium');

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, { code: promoCode.trim() });
      setPromoApplied(res.data);
      toast.success(`Promo applied — ${res.data.discount}% off!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid promo code');
      setPromoApplied(null);
    } finally { setPromoLoading(false); }
  };

  const removePromo = () => { setPromoApplied(null); setPromoCode(''); };

  const handleCheckout = async () => {
    const existingEventId = searchParams.get('event_id');
    // If no event yet, require name so the backend can create one. Date
    // and email are optional but strongly recommended.
    if (!existingEventId && !eventName.trim()) {
      toast.error('Please give your event a name.');
      return;
    }
    setLoading(true);
    try {
      const products = [...selectedProducts];
      const eventId = existingEventId;
      const eventPayload = eventId ? {} : {
        event_name: eventName.trim(),
        event_date: eventDate || undefined,
        email: email.trim() || undefined,
      };

      if (total === 0 && promoApplied) {
        const res = await axios.post(`${API}/funnel/checkout-free`, {
          products,
          promo_code: promoApplied.code,
          origin_url: window.location.origin,
          event_id: eventId || undefined,
          package: activePackage || undefined,
          ...eventPayload,
        });
        if (res.data.url) {
          window.location.href = res.data.url;
        } else {
          toast.error('Activation failed. Please try again.');
        }
      } else {
        const res = await axios.post(`${API}/funnel/checkout`, {
          products,
          origin_url: window.location.origin,
          event_id: eventId || undefined,
          package: activePackage || undefined,
          ...eventPayload,
        });
        if (res.data.url) {
          window.location.href = res.data.url;
        } else {
          toast.error('Checkout failed. Please try again.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#15161B] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-xl mx-auto px-6 py-16">
        <ConfettiBackground />
        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-white mb-10 transition-colors" data-testid="back-to-landing">
          <ArrowLeft size={18} weight="bold" />
          <span className="text-sm font-medium">Back</span>
        </button>

        <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="checkout-title">{activePackage ? PACKAGES[activePackage].name : 'Your Order'}</h1>
        <p className="text-gray-500 mb-10">{activePackage ? `${PACKAGES[activePackage].products.length} products included` : 'Instant access after purchase'}</p>

        {/* PACKAGE PRODUCTS or MAIN PRODUCT */}
        {activePackage ? (
          <>
            {PACKAGES[activePackage].products.map(id => {
              const p = PRODUCT_CATALOG[id];
              if (!p) return null;
              return (
                <div key={id} className="rounded-2xl border border-[#B89762]/50/30 bg-[#B89762]/5 p-5 mb-3" data-testid={`package-product-${id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: p.color }}>
                      {ICON_MAP[p.icon]}
                    </div>
                    <div>
                      <h3 className="font-bold">{p.name}</h3>
                      <p className="text-xs text-gray-400">{p.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <>
            {/* MAIN PRODUCT — Capture (always included) */}
            <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 mb-4" data-testid="main-product">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
                    <Camera size={24} weight="bold" className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">QR Photo Upload</h3>
                    <p className="text-sm text-gray-500">QR code + live gallery + unlimited uploads</p>
                  </div>
                </div>
                <p className="text-xl font-black">&pound;{PRODUCT_CATALOG.capture.price}</p>
              </div>
            </div>

            {/* SELECTED ADD-ONS */}
            {[...selectedProducts].filter(id => id !== 'capture').map(id => {
              const p = PRODUCT_CATALOG[id];
              if (!p) return null;
              return (
                <div key={id} className="rounded-2xl border-2 border-[#B89762]/50 bg-[#B89762]/10 p-5 mb-3" data-testid={`selected-product-${id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: p.color }}>
                        {ICON_MAP[p.icon]}
                      </div>
                      <div>
                        <h3 className="font-bold">{p.name}</h3>
                        <p className="text-xs text-gray-400">{p.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-lg font-black">&pound;{p.price}</p>
                      <button onClick={() => toggleProduct(id)} className="text-white/30 hover:text-red-400 transition-colors text-xs" data-testid={`remove-product-${id}`}>Remove</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* ADD MORE PRODUCTS (only in non-package mode) */}
        {!activePackage && addOnProducts.filter(([id]) => !selectedProducts.has(id)).length > 0 && (
          <div className="mb-6">
            <p className="text-sm text-gray-500 mb-3 mt-4">Add to your order:</p>
            <div className="space-y-2">
              {addOnProducts.filter(([id]) => !selectedProducts.has(id)).map(([id, p]) => (
                <div
                  key={id}
                  onClick={() => toggleProduct(id)}
                  className="rounded-2xl border border-white/10 bg-[#15161B] hover:border-white/20 p-4 cursor-pointer transition-all"
                  data-testid={`add-product-${id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}30` }}>
                        {ICON_MAP[p.icon]}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">{p.name}</h4>
                        <p className="text-xs text-gray-500">{p.desc}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-[#B89762]">+ &pound;{p.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EVENT DETAILS — only when no event_id in URL, required for checkout */}
        {!searchParams.get('event_id') && (
          <div className="mb-4 rounded-2xl border border-white/10 bg-[#15161B] p-5" data-testid="event-details-section">
            <h3 className="text-sm font-bold text-white mb-1">Your event details</h3>
            <p className="text-xs text-gray-500 mb-4">We'll create your event the moment checkout completes.</p>
            <div className="space-y-3">
              <input
                value={eventName}
                onChange={e => setEventName(e.target.value)}
                placeholder="Event name (e.g. Sarah & James Wedding)"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/40 transition-colors"
                data-testid="event-name-input"
              />
              <input
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/40 transition-colors"
                data-testid="event-date-input"
              />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email (for receipt + event link)"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/40 transition-colors"
                data-testid="event-email-input"
              />
            </div>
          </div>
        )}

        {/* PROMO CODE */}
        <div className="mb-4" data-testid="promo-section">
          {promoApplied ? (
            <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#B89762]/40/30" style={{ background: 'rgba(184,151,98,0.08)' }}>
              <div className="flex items-center gap-2">
                <Check size={16} weight="bold" className="text-[#B89762]" />
                <span className="text-[#B89762] text-sm font-semibold">{promoApplied.code} — {promoApplied.discount}% off</span>
              </div>
              <button onClick={removePromo} className="text-white/30 text-xs hover:text-white/60 transition-colors" data-testid="remove-promo">Remove</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                value={promoCode}
                onChange={e => setPromoCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && applyPromo()}
                placeholder="Promo code"
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#B89762]/40 transition-colors"
                data-testid="promo-input"
              />
              <button
                onClick={applyPromo}
                disabled={promoLoading || !promoCode.trim()}
                className="px-5 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-30 transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="apply-promo"
              >
                {promoLoading ? '...' : 'Apply'}
              </button>
            </div>
          )}
        </div>

        {/* TOTAL + CTA */}
        <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6" data-testid="checkout-total-section">
          {promoApplied && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/40 text-sm">Subtotal</span>
              <span className="text-white/40 text-sm line-through">&pound;{subtotal}</span>
            </div>
          )}
          {promoApplied && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-[#B89762] text-sm font-semibold">Discount ({promoApplied.discount}%)</span>
              <span className="text-[#B89762] text-sm font-semibold">-&pound;{promoDiscount}</span>
            </div>
          )}
          <div className="flex justify-between items-center mb-1">
            <span className="text-white/40 text-xs">{selectedProducts.size} item{selectedProducts.size > 1 ? 's' : ''}</span>
          </div>
          <div className="flex justify-between items-center mb-6">
            <span className="text-[#F4E9D2]/75 font-medium">Total</span>
            <span className="text-3xl font-black" data-testid="checkout-total">{total === 0 ? 'FREE' : `£${total}`}</span>
          </div>

          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#B89762]/20 flex items-center justify-center gap-3"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="checkout-button"
          >
            <ShoppingCart size={22} weight="bold" />
            {loading ? 'Processing...' : (total === 0 ? 'Activate Free Access' : 'Complete Purchase')}
          </button>

          {total > 0 && (
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
              <ShieldCheck size={16} weight="bold" />
              <span>Secure payment via Stripe</span>
            </div>
          )}
        </div>

        {/* TRUST ELEMENTS */}
        <div className="mt-8 space-y-3">
          {['Instant access after payment', 'No subscriptions or hidden fees', 'Works on any device'].map((t, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-gray-500">
              <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
                <Check size={10} weight="bold" className="text-white" />
              </div>
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreCheckout;
